import React, { useMemo, useState } from 'react';

import { DeleteConfirmationModal, type SelectOption } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { PhaseFormModal } from './PhaseFormModal';
import { TaskFormModal } from './TaskFormModal';
import type { DraftTask } from '../../hooks/useProjectDraft';
import type { DependencyMode, ProjectPhase } from '../../types';

/** `yyyy-mm-dd` → `dd/mm` (parse local, sem shift de fuso). */
function fmtDate(d?: string): string {
  if (!d) return '—';
  const parsed = new Date(`${d}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

const MODE_LABEL: Record<DependencyMode, string> = {
  inform: 'Informar',
  warn: 'Avisar',
  block: 'Bloquear',
};

export interface PhaseAccordionProps {
  phase: ProjectPhase;
  /** Tarefas desta fase (na ordem do rascunho). */
  phaseTasks: DraftTask[];
  /** Todas as tarefas do rascunho (para o Select de dependência). */
  allTasks: DraftTask[];
  /** Opções de responsável (líder + membros). */
  userOptions: SelectOption[];
  /** userId → nome (para exibição do responsável). */
  usersById: Map<string, { id: string; name: string }>;
  /** Se pode remover a fase (há mais de uma). */
  canRemove: boolean;
  onUpdatePhase: (patch: { name?: string; color?: string }) => void;
  onRemove: () => void;
  onAddTask: (task: Omit<DraftTask, 'localId' | 'phaseId'>) => void;
  onRemoveTask: (localId: string) => void;
}

export const PhaseAccordion: React.FC<PhaseAccordionProps> = ({
  phase,
  phaseTasks,
  allTasks,
  userOptions,
  usersById,
  canRemove,
  onUpdatePhase,
  onRemove,
  onAddTask,
  onRemoveTask,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);

  const titleByLocalId = useMemo(() => {
    const map = new Map<string, string>();
    allTasks.forEach((t) => map.set(t.localId, t.title.trim() || 'Tarefa sem título'));
    return map;
  }, [allTasks]);

  const depOptions = useMemo<SelectOption[]>(
    () => allTasks.map((t) => ({ value: t.localId, label: t.title.trim() || 'Tarefa sem título' })),
    [allTasks],
  );

  const responsibleOptions = useMemo<SelectOption[]>(
    () => [{ value: '', label: 'Sem responsável' }, ...userOptions],
    [userOptions],
  );

  const period = useMemo(() => {
    const dated = phaseTasks.filter((t) => t.startDate && t.endDate);
    if (dated.length === 0) return null;
    const start = dated.reduce((min, t) => (t.startDate < min ? t.startDate : min), dated[0]!.startDate);
    const end = dated.reduce((max, t) => (t.endDate > max ? t.endDate : max), dated[0]!.endDate);
    return `${fmtDate(start)} – ${fmtDate(end)}`;
  }, [phaseTasks]);

  return (
    <div
      className="rounded-lg border overflow-hidden"
      style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)' }}
      data-testid={`project-form-phase-${phase.id}`}
    >
      {/* Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded((e) => !e)}
        data-testid={`project-form-phase-toggle-${phase.id}`}
      >
        <Icon
          name="chevron-down"
          className="w-4 h-4 flex-shrink-0 transition-transform"
          style={{ color: 'var(--theme-muted-text)', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
        />
        <span
          className="inline-block rounded-full flex-shrink-0"
          style={{ width: 12, height: 12, backgroundColor: phase.color }}
        />
        <span
          className="flex-1 min-w-0 truncate font-medium text-sm"
          style={{ color: 'var(--theme-card-text)' }}
          data-testid={`project-form-phase-name-${phase.id}`}
        >
          {phase.name || 'Fase sem nome'}
        </span>
        <span className="text-xs flex-shrink-0" style={{ color: 'var(--theme-muted-text)' }}>
          {phaseTasks.length} {phaseTasks.length === 1 ? 'tarefa' : 'tarefas'}
          {period ? ` · ${period}` : ''}
        </span>
        {/* Editar fase (nome + cor) — abre modal (#179) */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setPhaseModalOpen(true);
          }}
          className="p-1.5 rounded-lg transition-colors flex-shrink-0"
          style={{ color: 'var(--theme-muted-text)' }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          title="Editar fase"
          data-testid={`project-form-phase-edit-${phase.id}`}
        >
          <Icon name="edit" className="h-[18px] w-[18px]" />
        </button>
        {canRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (phaseTasks.length > 0) setDeleteOpen(true);
              else onRemove();
            }}
            className="p-1.5 rounded-lg transition-colors flex-shrink-0"
            style={{ color: 'var(--theme-muted-text)' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            title="Remover fase"
            data-testid={`project-form-phase-remove-${phase.id}`}
          >
            <Icon name="trash" className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-2">
          {phaseTasks.length === 0 && (
            <p className="text-sm py-2" style={{ color: 'var(--theme-muted-text)' }}>
              Nenhuma tarefa nesta fase ainda.
            </p>
          )}

          {phaseTasks.map((task) => {
            const assignee = task.assigneeId ? usersById.get(task.assigneeId) : undefined;
            return (
              <div
                key={task.localId}
                className="flex items-center gap-3 flex-wrap rounded-lg border px-3 py-2"
                style={{ borderColor: 'var(--theme-card-border)' }}
                data-testid={`project-form-task-${task.localId}`}
              >
                {task.isMilestone && (
                  <span
                    className="flex-shrink-0"
                    style={{ width: 10, height: 10, borderRadius: 2, transform: 'rotate(45deg)', backgroundColor: '#d97706' }}
                    title="Marco"
                  />
                )}
                <span className="text-sm font-medium truncate" style={{ color: 'var(--theme-card-text)' }}>
                  {task.title.trim() || 'Tarefa sem título'}
                </span>

                {task.dependencies.map((dep) => (
                  <span
                    key={dep.localId}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs"
                    style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
                    title={`Depende de: ${titleByLocalId.get(dep.localId) ?? '—'} (${MODE_LABEL[dep.mode]})`}
                  >
                    <Icon name="link" className="w-3 h-3" />
                    {titleByLocalId.get(dep.localId) ?? '—'} · {MODE_LABEL[dep.mode]}
                  </span>
                ))}

                <span className="text-xs flex items-center gap-1" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="calendar" className="w-3 h-3" />
                  {task.isMilestone ? fmtDate(task.startDate) : `${fmtDate(task.startDate)} – ${fmtDate(task.endDate)}`}
                </span>

                <span className="text-xs ml-auto flex items-center gap-1" style={{ color: 'var(--theme-muted-text)' }}>
                  <Icon name="user" className="w-3 h-3" />
                  {assignee ? assignee.name : 'sem resp.'}
                </span>

                <button
                  type="button"
                  onClick={() => onRemoveTask(task.localId)}
                  className="p-1 rounded-lg transition-colors flex-shrink-0"
                  style={{ color: 'var(--theme-muted-text)' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--theme-button-ghost-hover)')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  title="Remover tarefa"
                  data-testid={`project-form-task-remove-${task.localId}`}
                >
                  <Icon name="trash" className="w-4 h-4" />
                </button>
              </div>
            );
          })}

          {/* Nova tarefa — abre modal (#178) */}
          <button
            type="button"
            onClick={() => setTaskModalOpen(true)}
            className="flex items-center gap-1.5 text-sm font-medium py-1.5 transition-opacity hover:opacity-80"
            style={{ color: 'var(--theme-primary)' }}
            data-testid={`project-form-task-add-${phase.id}`}
          >
            <Icon name="plus" className="w-4 h-4" />
            Tarefa nesta fase
          </button>
        </div>
      )}

      <TaskFormModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        phaseName={phase.name || 'Fase sem nome'}
        responsibleOptions={responsibleOptions}
        depOptions={depOptions}
        onAdd={onAddTask}
      />

      <PhaseFormModal
        isOpen={phaseModalOpen}
        onClose={() => setPhaseModalOpen(false)}
        initialName={phase.name}
        initialColor={phase.color}
        onSave={onUpdatePhase}
      />

      <DeleteConfirmationModal
        isOpen={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false);
          onRemove();
        }}
        title="Remover fase"
        message={`Remover a fase "${phase.name}"?`}
        warningMessage={`As ${phaseTasks.length} tarefa(s) desta fase serão removidas do rascunho.`}
        confirmButtonText="Remover fase"
      />
    </div>
  );
};

PhaseAccordion.displayName = 'PhaseAccordion';
